export interface SupplierItemAliasRecord {
  raw_description_norm: string;
  food_id: string;
  supplier_code: string | null;
  occurrences: number;
  last_used_at: string;
  source: string;
  pack_factor: number | null;
  pack_uom: string | null;
  base_uom: string | null;
}

export interface SupplierItemMatchRow {
  key: string;
  foodId: string;
  supplierCode: string | null;
  descriptions: string[];
  occurrences: number;
  lastUsedAt: string;
  source: string;
  packFactor: number | null;
  packUom: string | null;
  baseUom: string | null;
  baseUoms: string[];
  hasCodeConflict: boolean;
  hasBaseUomConflict: boolean;
}

function normalizedCode(value: string | null): string | null {
  const normalized = value?.trim().toUpperCase().replace(/\s+/g, ' ') ?? '';
  return normalized || null;
}

export function buildSupplierItemMatchRows(
  aliases: readonly SupplierItemAliasRecord[],
): SupplierItemMatchRow[] {
  const grouped = new Map<string, SupplierItemMatchRow>();
  const foodsByCode = new Map<string, Set<string>>();

  for (const alias of aliases) {
    const supplierCode = normalizedCode(alias.supplier_code);
    const description = alias.raw_description_norm.trim();
    const identity = supplierCode ?? description.toLocaleLowerCase('it-IT');
    const key = `${supplierCode ? 'code' : 'description'}:${identity}|${alias.food_id}`;
    const existing = grouped.get(key);

    if (supplierCode) {
      const foodIds = foodsByCode.get(supplierCode) ?? new Set<string>();
      foodIds.add(alias.food_id);
      foodsByCode.set(supplierCode, foodIds);
    }

    if (!existing) {
      grouped.set(key, {
        key,
        foodId: alias.food_id,
        supplierCode,
        descriptions: description ? [description] : [],
        occurrences: Math.max(0, alias.occurrences || 0),
        lastUsedAt: alias.last_used_at,
        source: alias.source,
        packFactor: alias.pack_factor,
        packUom: alias.pack_uom,
        baseUom: alias.base_uom,
        baseUoms: alias.base_uom ? [alias.base_uom.trim().toLowerCase()] : [],
        hasCodeConflict: false,
        hasBaseUomConflict: false,
      });
      continue;
    }

    if (description && !existing.descriptions.includes(description)) {
      existing.descriptions.push(description);
    }
    existing.occurrences += Math.max(0, alias.occurrences || 0);
    const isMoreRecent = alias.last_used_at > existing.lastUsedAt;
    if (isMoreRecent) {
      existing.lastUsedAt = alias.last_used_at;
    }
    if (
      alias.source === 'manual'
      || (alias.source === 'first_setup' && existing.source !== 'manual')
    ) {
      existing.source = alias.source;
    }
    const hasCompleteFactor = alias.pack_factor != null
      && alias.pack_factor > 0
      && !!alias.pack_uom
      && !!alias.base_uom;
    if (hasCompleteFactor && (existing.packFactor == null || isMoreRecent)) {
      existing.packFactor = alias.pack_factor;
      existing.packUom = alias.pack_uom;
      existing.baseUom = alias.base_uom;
    }
    const baseUom = alias.base_uom?.trim().toLowerCase();
    if (baseUom && !existing.baseUoms.includes(baseUom)) {
      existing.baseUoms.push(baseUom);
    }
  }

  return [...grouped.values()]
    .map((row) => ({
      ...row,
      hasCodeConflict: row.supplierCode != null
        && (foodsByCode.get(row.supplierCode)?.size ?? 0) > 1,
      hasBaseUomConflict: row.baseUoms.length > 1,
    }))
    .sort((left, right) => (
      right.lastUsedAt.localeCompare(left.lastUsedAt)
      || (left.supplierCode ?? left.descriptions[0] ?? '').localeCompare(
        right.supplierCode ?? right.descriptions[0] ?? '',
        'it',
      )
    ));
}

export function supplierMatchSourceLabel(source: string): string {
  if (source === 'manual') return 'Confermato manualmente';
  if (source === 'first_setup') return 'Configurazione iniziale';
  if (source === 'ai_match') return 'Abbinamento storico';
  return 'Collegamento salvato';
}

export function supplierPackFactorLabel(row: SupplierItemMatchRow): string | null {
  if (
    row.packFactor == null
    || row.packFactor <= 0
    || !row.packUom
    || !row.baseUom
  ) return null;

  const factor = new Intl.NumberFormat('it-IT', {
    maximumFractionDigits: 4,
  }).format(row.packFactor);
  return `1 ${row.packUom} = ${factor} ${row.baseUom}`;
}
